<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { ArrowRight, X } from 'lucide-vue-next'
import ReleaseNotes from '@/components/ReleaseNotes.vue'
import { latestRelease, formatReleaseDate } from '@/data/changelog'
import { ANNOUNCEMENT_STORAGE_KEY, announcementHistory, withAnnouncementLock } from '@/services/update-announcement'

const props = defineProps<{ ready: boolean; blocked: boolean }>()
const emit = defineEmits<{ 'occupancy-change': [occupied: boolean] }>()
const { t, locale } = useI18n()
const router = useRouter()
const visible = ref(false)
const closing = ref(false)
const seen = ref(!latestRelease || announcementHistory.hasSeen(latestRelease.id))
const pageVisible = ref(document.visibilityState === 'visible')
const eligible = computed(() => props.ready && !props.blocked && pageVisible.value)
watch([seen, visible, closing], () => emit('occupancy-change', !seen.value || visible.value || closing.value), { immediate: true })
let disposed = false
let requesting = false
let navigateToHistory = false
let controller: AbortController | undefined
let finishDisplay: (() => void) | undefined
let finishCloseWait: (() => void) | undefined
let previousFocus: HTMLElement | null = null

function releaseLock(): void {
  finishDisplay?.()
  finishDisplay = undefined
}

async function checkAnnouncement(): Promise<void> {
  await nextTick()
  const release = latestRelease
  if (disposed || requesting || closing.value || visible.value || !eligible.value || !release) return
  seen.value = announcementHistory.hasSeen(release.id)
  if (seen.value) return
  requesting = true
  controller = new AbortController()
  try {
    await withAnnouncementLock(async () => {
      if (disposed || !eligible.value || announcementHistory.hasSeen(release.id)) return
      previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
      // Hold the cross-tab lock until the dialog is visible and its ID is persisted.
      await new Promise<void>((resolve) => {
        finishDisplay = resolve
        visible.value = true
      })
    }, controller.signal)
  } finally {
    requesting = false
    controller = undefined
    if (!disposed && eligible.value && !visible.value && !closing.value && !announcementHistory.hasSeen(release.id)) {
      void checkAnnouncement()
    }
  }
}

function markOpened(): void {
  if (visible.value && eligible.value && latestRelease) {
    announcementHistory.markDisplayed(latestRelease.id)
    seen.value = true
  }
  releaseLock()
}

function dismiss(showHistory = false): void {
  // An explicit dismissal during the opening transition also counts as having seen it.
  if (!visible.value) return
  closing.value = true
  if (latestRelease) announcementHistory.markDisplayed(latestRelease.id)
  seen.value = true
  navigateToHistory = showHistory
  visible.value = false
}

function waitForClosePaint(): Promise<void> {
  if (!pageVisible.value) return Promise.resolve()
  return new Promise((resolve) => {
    let frame: number
    finishCloseWait = () => {
      cancelAnimationFrame(frame)
      finishCloseWait = undefined
      resolve()
    }
    // Allow the closed dialog to paint before focus, navigation, or the next guide.
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => finishCloseWait?.())
    })
  })
}

async function afterClosed(): Promise<void> {
  releaseLock()
  if (disposed) return
  await waitForClosePaint()
  if (disposed) return
  try {
    if (navigateToHistory) {
      navigateToHistory = false
      await router.push({ name: 'changelog' })
      if (!disposed && pageVisible.value) {
        document.querySelector<HTMLElement>('#main-content')?.focus({ preventScroll: true })
      }
    } else if (!props.blocked && pageVisible.value) {
      const target = previousFocus?.isConnected && previousFocus !== document.body
        ? previousFocus
        : document.querySelector<HTMLElement>('#main-content')
      target?.focus({ preventScroll: true })
    }
  } finally {
    if (!disposed) closing.value = false
  }
  await nextTick()
  void checkAnnouncement()
}

function onVisibilityChange(): void {
  pageVisible.value = document.visibilityState === 'visible'
  if (!pageVisible.value) finishCloseWait?.()
}
function onStorageChange(event: StorageEvent): void {
  if (event.key === ANNOUNCEMENT_STORAGE_KEY || event.key === null) {
    seen.value = !latestRelease || announcementHistory.hasSeen(latestRelease.id)
  }
}

watch(eligible, (allowed) => {
  if (allowed) void checkAnnouncement()
  else {
    controller?.abort()
    if (visible.value) {
      closing.value = true
      visible.value = false
    }
    releaseLock()
  }
}, { immediate: true, flush: 'post' })

onMounted(() => {
  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('storage', onStorageChange)
})
onBeforeUnmount(() => {
  disposed = true
  finishCloseWait?.()
  controller?.abort()
  releaseLock()
  document.removeEventListener('visibilitychange', onVisibilityChange)
  window.removeEventListener('storage', onStorageChange)
})
</script>

<template>
  <el-dialog
    v-if="latestRelease"
    :model-value="visible"
    class="update-announcement-dialog"
    modal-class="announcement-overlay"
    transition="announcement-fade"
    width="42rem"
    align-center
    append-to-body
    destroy-on-close
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    @update:model-value="(value: boolean) => { if (!value) dismiss() }"
    @opened="markOpened"
    @close="closing = true"
    @closed="afterClosed"
  >
    <template #header="{ titleId }">
      <div class="announcement-header">
        <div class="announcement-heading">
          <h2 :id="titleId">{{ t('changelog.announcement.title') }}</h2>
          <time :datetime="latestRelease.date">{{ formatReleaseDate(latestRelease.date, locale) }}</time>
        </div>
        <button class="icon-btn announcement-close" type="button" :aria-label="t('changelog.announcement.close')" :title="t('changelog.announcement.close')" @click="dismiss()"><X aria-hidden="true" /></button>
      </div>
    </template>
    <div class="announcement-release">
      <ReleaseNotes :release="latestRelease" />
    </div>
    <template #footer>
      <div class="announcement-actions">
        <el-button text class="announcement-history" @click="dismiss(true)">{{ t('changelog.announcement.history') }}<ArrowRight aria-hidden="true" /></el-button>
        <el-button type="primary" @click="dismiss()">{{ t('changelog.announcement.dismiss') }}</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style>
.announcement-overlay.announcement-fade-enter-active,
.announcement-overlay.announcement-fade-leave-active { transition: opacity 200ms ease; will-change: opacity; }
.announcement-overlay.announcement-fade-enter-from,
.announcement-overlay.announcement-fade-leave-to { opacity: 0; }
.el-dialog.update-announcement-dialog { display: flex; flex-direction: column; width: min(42rem, calc(100vw - 2rem)); max-height: min(52rem, calc(100dvh - 3rem)); padding: 0; border: 0.0625rem solid var(--border); border-radius: 0.5rem; overflow: hidden; background: var(--surface); }
.update-announcement-dialog .el-dialog__header { flex: none; padding: 1.25rem 1.5rem; margin: 0; border-bottom: 0.0625rem solid var(--border); }
.update-announcement-dialog .el-dialog__body { flex: 1 1 auto; min-height: 0; padding: 1.25rem 1.5rem; overflow-y: auto; overscroll-behavior: contain; scrollbar-width: thin; scrollbar-color: var(--border-strong) transparent; scrollbar-gutter: stable; }
.update-announcement-dialog .el-dialog__footer { flex: none; padding: 1rem 1.5rem; border-top: 0.0625rem solid var(--border); }
.announcement-header { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
.announcement-header h2 { margin: 0; color: var(--text-strong); font-size: 1.125rem; font-weight: 650; line-height: 1.5; }
.announcement-heading { display: grid; gap: 0.25rem; min-width: 0; }
.announcement-close { flex: none; width: 2.75rem; height: 2.75rem; margin-right: -0.5rem; border-color: transparent; background: transparent; box-shadow: none; }
.announcement-close svg { width: 1.125rem; height: 1.125rem; }
.announcement-heading time { color: var(--muted-strong); font-size: 0.8125rem; line-height: 1.5; font-variant-numeric: tabular-nums; }
.announcement-release .change-group { margin-top: 0; }
.announcement-release .change-group + .change-group { margin-top: 1.25rem; padding-top: 1.25rem; border-top: 0.0625rem solid var(--border); }
.announcement-release .change-group h3 { margin-bottom: 0.875rem; gap: 0.5rem; }
.announcement-release .change-list { padding: 0 0 0 1.75rem; gap: 0.875rem; list-style: disc; }
.announcement-release .change-list li { display: list-item; padding-left: 0.25rem; min-width: 0; line-height: 1.65; }
.announcement-release .change-list strong { display: block; margin: 0; font-size: 0.875rem; }
.announcement-release .change-list li > span { display: block; margin-top: 0.25rem; color: var(--muted-strong); font-size: 0.8125rem; }
.announcement-actions { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; }
.announcement-actions .el-button { min-height: 2.25rem; }
.announcement-actions .el-button--primary { box-shadow: none; }
.announcement-actions .announcement-history { padding-inline: 0.375rem; box-shadow: none; }
.announcement-history svg { width: 0.9375rem; height: 0.9375rem; }
@media (max-width: 30rem) {
  .el-dialog.update-announcement-dialog { max-height: calc(100dvh - 1.5rem); width: calc(100vw - 1.5rem); }
  .update-announcement-dialog .el-dialog__header { padding: 1rem 1.125rem; }
  .update-announcement-dialog .el-dialog__body { padding: 1.25rem 1.125rem; }
  .update-announcement-dialog .el-dialog__footer { padding: 0.875rem 1.125rem; }
  .announcement-actions { flex-wrap: wrap; }
  .announcement-actions .el-button > span { white-space: normal; }
}

</style>
